'use client';

import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarRail } from '@zond/ui';
import { Blocks, FileText, FolderKanban, LayoutDashboard, MessageSquare, Settings2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AllowedAccess, usePermission } from 'react-permission-role';
import { useLocation } from 'react-router-dom';
import { DISABLE_WORKSPACE_ACTIVATION_CHECK } from '../../../modules/dashboard/constant';
import { useGetCurrentMember } from '../../../modules/members/members.queries';
import { useGetProjects } from '../../../modules/projects/projects.queries';
import { useStore } from '../../../store';
import { DownloadClientWidget } from './download-client-widget';
import { createNavConfig } from './nav-config';
import { NavMain } from './nav-main';
import { NavUser } from './nav-user';
import { InviteUsersPrompt } from './team-members-widget';
import { TeamSwitcher } from './team-switcher';

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation('navigation');
  const selectedWorkspaceId = useStore((state) => state.getSelectedWorkspaceId());
  const selectedProjectId = useStore((state) => state.selectedProjectId);
  const user = useStore((state) => state.user);
  const currentWorkspace = useStore((state) => state.getCurrentWorkspace());
  const location = useLocation();
  const currentPath = location.pathname;
  const { data: projects } = useGetProjects();
  const { data: member } = useGetCurrentMember();
  const { setUser } = usePermission();
  const [key, setKey] = useState(0);

  const hasProjects = Boolean(projects && projects.length > 0);
  const projectExists = selectedProjectId && projects?.some((p) => String(p.id) === selectedProjectId);

  const { adminNavItems, userNavItems } = createNavConfig(
    String(selectedWorkspaceId),
    currentPath,
    hasProjects,
    projectExists || false,
    selectedProjectId,
    t
  );

  // Force re-render when member roles change
  useEffect(() => {
    if (member) {
      setKey((prev) => prev + 1);
    }
  }, [member?.roles]);

  return (
    <Sidebar collapsible="icon" {...props} key={key}>
      <SidebarHeader>
        <TeamSwitcher />
      </SidebarHeader>
      <SidebarContent>
        {currentWorkspace?.activated || DISABLE_WORKSPACE_ACTIVATION_CHECK ? (
          <>
            <AllowedAccess roles={['ADMIN']}>
              <NavMain items={adminNavItems} />
            </AllowedAccess>
            <AllowedAccess roles={['USER']}>
              <NavMain items={userNavItems} />
            </AllowedAccess>
          </>
        ) : null}
      </SidebarContent>
      <AllowedAccess roles={['ADMIN']}>
        <InviteUsersPrompt />
      </AllowedAccess>
      <SidebarFooter className="border-t">
        <NavUser
          user={{
            name:
              user?.firstname && user?.lastname
                ? `${user.firstname} ${user.lastname}`
                : user?.email?.split('@')[0] || t('user.unknown'),
            email: user?.email || '',
            avatar: user?.avatar || '/avatars/default.jpg',
          }}
        />
      </SidebarFooter>
      {(currentWorkspace?.activated || DISABLE_WORKSPACE_ACTIVATION_CHECK) && <DownloadClientWidget />}
      <SidebarRail />
    </Sidebar>
  );
}
